import axios from "axios";
import { useAtom, useAtomValue } from "jotai";
import { useState, useRef, useCallback, useEffect } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { socketDesignCreatedMessage } from "../../hooks/useSocket";
import { useLogin } from "../login";
import {
    dcColorsAtom,
    dcCommentsAtom,
    dcDesignsAtom,
    dcInsightAtom,
    dcLoadingAtom,
    dcVersionsAtom,
    dcFilterAtomFamily,
    dcSortAtomFamily,
    dcPaginationAtomFamily,
    dcDesignsGroupedByCollectionAtomFamily,
    dcDesignsGroupedByStatusAtomFamily,
    dcViewModeWriteAtom,
    DESIGN_LIST_SCOPE_STUDIO,
    DESIGN_LIST_SCOPE_ORDER_PICKER,
    DesignListScope,
    DCDesign,
    DCDesignColor,
    DCDesignComment,
    DCDesignInsight,
    DCDesignScaleOption,
    DCDesignVersion,
    DCDesignCreateInput,
    DCDesignFilters,
    DCDesignSort,
    DCDesignPaginatedNode,
    DCVariantLinkCandidateResult,
    DesignListViewMode,
    isDesignAnalyzing
} from "./types";

const gqlEndpoint = `${environment.baseUrl}/graphql`;

const versionFields = `id design_id version_label image_url preview_url dpi width_px height_px width_in height_in notes created_by created_at swatch_fill_mode needs_swatch_fill_choice`;
const licensingFields = `id design_id model percentage_value flat_amount flat_interval currency`;
const designFields = `id brand_id title pattern_type collection_id pricing_template_id description_template_id status current_version_id shopify_product_id listing_images_disabled infinity_mural_mode is_catalog_design catalog_published created_at updated_at licensing { ${licensingFields} } current_version { ${versionFields} }`;
const commentFields = `id design_id user_id body type needs_attention attention_resolved attention_resolved_at attention_resolved_by attention_resolved_by_user { first last } event_data created_at user { first last }`;
const colorFields = `id design_id name hex is_primary coverage_pct created_at`;
const insightFields = `id design_id popularity_score popularity_reason target_audiences recommended_rooms style_tags theme_tags mood_tags medium_tags misc_tags color_tags room_tags dominant_category alt_categories alternative_names wallpaper_description fabric_description wrapping_paper_description marketing_description created_at`;
const paginatedNodeFields = `id brand_id title status pattern_type collection_id pricing_template_id description_template_id current_version_id shopify_product_id infinity_mural_mode created_at updated_at has_unresolved_attention has_default_wallpaper_scale has_selected_sample is_setup_complete current_version { id design_id version_label image_url preview_url dpi width_px height_px width_in height_in notes created_by created_at swatch_fill_mode needs_swatch_fill_choice } collection { id brand_id name is_artist_based is_global square_logo_url rectangular_logo_url created_at updated_at } release { id design_id status design_peel_rpe design_luxe_rpe design_wrap_rpe sample_peel_rpe sample_luxe_rpe completion_percentage released_by created_at updated_at } created_by created_by_user { id first last } insight { id } is_licensed owner_brand_name is_catalog_design catalog_published is_variant_member_readonly licensing { ${licensingFields} }`
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, {
        headers: { 'X-Wallmates-Auth': authToken }
    });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
};

type UseDesignCreator = [
    {
        designs: DCDesign[];
        versions: DCDesignVersion[];
        comments: DCDesignComment[];
        colors: DCDesignColor[];
        insight: DCDesignInsight | null;
        loading: boolean;
        paginatedDesigns: DCDesignPaginatedNode[];
        paginationLoading: boolean;
        hasMore: boolean;
        filters: DCDesignFilters;
        sort: DCDesignSort;
        groupedDesigns: Record<string, DCDesignPaginatedNode[]>;
        groupedByStatus: Record<string, DCDesignPaginatedNode[]>;
        viewMode: DesignListViewMode;
    },
    {
        setViewMode: (mode: DesignListViewMode) => void;
        listDesigns: (brand_id?: number) => Promise<DCDesign[] | null>;
        listVariantLinkCandidates: (brand_id: number, design_id: number, search?: string, first?: number) => Promise<DCVariantLinkCandidateResult | null>;
        getDesign: (id: number) => Promise<DCDesign | null>;
        createDesign: (input: DCDesignCreateInput) => Promise<DCDesign | null>;
        updateDesign: (id: number, input: Partial<DCDesign>) => Promise<DCDesign | null>;
        deleteDesign: (id: number) => Promise<boolean | null | { hasOrders: true; orderCount: number }>;
        forceDeleteDesign: (id: number) => Promise<boolean | null>;

        listVersions: (design_id: number) => Promise<DCDesignVersion[] | null>;
        createVersion: (input: Partial<DCDesignVersion>) => Promise<DCDesignVersion | null>;
        deleteVersion: (id: number, design_id: number) => Promise<boolean | null>;
        setCurrentVersion: (design_id: number, version_id: number) => Promise<DCDesign | null>;

        listComments: (design_id: number) => Promise<DCDesignComment[] | null>;
        addComment: (input: Partial<DCDesignComment> & { needs_attention?: boolean }) => Promise<DCDesignComment | null>;
        deleteComment: (id: number) => Promise<boolean | null>;
        resolveComment: (id: number, resolvedBy?: number) => Promise<DCDesignComment | null>;
        unresolveComment: (id: number) => Promise<DCDesignComment | null>;

        listColors: (design_id: number) => Promise<DCDesignColor[] | null>;
        replaceColors: (design_id: number, colors: Partial<DCDesignColor>[]) => Promise<DCDesignColor[] | null>;

        getInsight: (design_id: number) => Promise<DCDesignInsight | null>;
        upsertInsight: (input: Partial<DCDesignInsight>) => Promise<DCDesignInsight | null>;

        getScaleOptions: (width_px: number, height_px: number, dpi: number) => Promise<number[] | null>;
        listDesignScaleOptions: (design_id: number) => Promise<DCDesignScaleOption[] | null>;
        upsertDesignScaleOptions: (design_id: number, options: Partial<DCDesignScaleOption>[], replace?: boolean) => Promise<DCDesignScaleOption[] | null>;
        fetchDistinctWallpaperScales: (brand_id?: number) => Promise<number[] | null>;
        generateAnalysis: (params: { design_id: number; image_url: string; width_px: number; height_px: number; dpi: number }) => Promise<{ colors: DCDesignColor[]; insight: DCDesignInsight } | null>;
        convertToProduct: (design_id: number) => Promise<DCDesign | null>;
        uploadDesignImage: (file: File) => Promise<string | null>;
        reuploadDesignFile: (designId: number, file: File) => Promise<DCDesign | null>;
        hasActiveOrders: (designId: number) => Promise<boolean | null>;
        fetchDesignsPaginated: (brand_id?: number, loadMore?: boolean, overrides?: { filters?: DCDesignFilters; sort?: DCDesignSort }) => Promise<void>;
        updateFilters: (newFilters: Partial<DCDesignFilters>, brandId?: number) => void;
        updateSort: (newSort: Partial<DCDesignSort>, brandId?: number) => void;
        resetPagination: () => void;
        fetchDesignsByCollection: (collectionId: number | null, cursor?: string | null, pageSize?: number) => Promise<{ designs: DCDesignPaginatedNode[]; endCursor: string | null; hasNextPage: boolean }>;
        fetchDesignCreators: (brand_id?: number) => Promise<{ id: number; first: string | null; last: string | null }[]>;
        checkTitleUniqueness: (brandId: number, title: string, excludeDesignId?: number) => Promise<{ count: number } | null>;
    }
];

/**
 * @param scope Which design list's filter/sort/pagination state to bind to. Defaults
 *              to the design studio's, so existing call sites are unchanged. The order
 *              flow's picker passes its own scope so the two lists cannot fight over
 *              one another's filters or accumulated pages.
 */
export const useDesignCreator = (scope: DesignListScope = DESIGN_LIST_SCOPE_STUDIO): UseDesignCreator => {
    const [designs, setDesigns] = useAtom(dcDesignsAtom);
    const [versions, setVersions] = useAtom(dcVersionsAtom);
    const [comments, setComments] = useAtom(dcCommentsAtom);
    const [colors, setColors] = useAtom(dcColorsAtom);
    const [insight, setInsight] = useAtom(dcInsightAtom);
    const [loading, setLoading] = useAtom(dcLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [user, authToken, loginFunctions] = useLogin();
    const [filters, setFilters] = useAtom(dcFilterAtomFamily(scope));
    const [sort, setSort] = useAtom(dcSortAtomFamily(scope));
    const [pagination, setPagination] = useAtom(dcPaginationAtomFamily(scope));
    const groupedDesigns = useAtomValue(dcDesignsGroupedByCollectionAtomFamily(scope));
    const groupedByStatus = useAtomValue(dcDesignsGroupedByStatusAtomFamily(scope));
    const [viewMode, setViewMode] = useAtom(dcViewModeWriteAtom);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const latestRequestIdRef = useRef(0);
    const designCreatedMsg = useAtomValue(socketDesignCreatedMessage);
    const pollingIntervalsRef = useRef<Map<number, ReturnType<typeof setInterval>>>(new Map());
    const pollingMaxTimeoutsRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

    // #region handleDesignCreatedWS
    // When a design is created via the external API, the backend broadcasts a WS message.
    // Prepend it to the paginated list so the user sees a skeleton loader immediately.
    useEffect(() => {
        if (!designCreatedMsg?.payload || !user?.brand_id) return;
        const payload = designCreatedMsg.payload;

        // Only show designs for the user's brand
        if (payload.brand_id !== user.brand_id) return;

        // Evaluate against current filters
        if (filters.status.length > 0 && !filters.status.includes(payload.status || 'in_progress_0')) return;
        if (filters.pattern_type.length > 0 && !filters.pattern_type.includes(payload.pattern_type)) return;
        if (filters.released === 'RELEASED') return; // New designs are never released
        if (filters.title_search && !payload.title?.toLowerCase().includes(filters.title_search.toLowerCase())) return;
        if (filters.needs_attention) return; // New designs never have unresolved attention
        if (filters.created_by.length > 0 && (!payload.created_by || !filters.created_by.includes(payload.created_by))) return;

        // Build a DCDesignPaginatedNode from the WS payload
        const newDesign: DCDesignPaginatedNode = {
            id: payload.id,
            brand_id: payload.brand_id,
            title: payload.title,
            status: payload.status || 'in_progress_0',
            pattern_type: payload.pattern_type ?? null,
            collection_id: payload.collection_id ?? null,
            pricing_template_id: null,
            description_template_id: null,
            current_version_id: payload.current_version?.id ?? null,
            shopify_product_id: null,
            created_at: payload.created_at || new Date().toISOString(),
            updated_at: payload.created_at || new Date().toISOString(),
            current_version: payload.current_version ? {
                id: payload.current_version.id,
                design_id: payload.id,
                version_label: 'v1',
                image_url: payload.current_version.image_url,
                preview_url: payload.current_version.preview_url ?? null,
                created_at: payload.created_at,
            } : null,
            collection: null,
            release: null,
            has_unresolved_attention: false,
            created_by: payload.created_by ?? null,
            created_by_user: null,
            insight: null,
        };

        // Prepend to pagination, avoiding duplicates
        setPagination((prev) => {
            if (prev.designs.some((d) => d.id === newDesign.id)) return prev;
            return { ...prev, designs: [newDesign, ...prev.designs] };
        });
    }, [designCreatedMsg]);

    // #region pollAnalyzingDesigns
    // Poll designs that are still analyzing (status=in_progress_0, no insight) every 4 seconds.
    // When analysis completes, update the design in-place and stop polling.
    useEffect(() => {
        const analyzingDesigns = pagination.designs.filter(isDesignAnalyzing);
        const activeIds = new Set(analyzingDesigns.map((d) => d.id));

        // Stop polling for designs no longer analyzing
        for (const [id, interval] of pollingIntervalsRef.current) {
            if (!activeIds.has(id)) {
                clearInterval(interval);
                pollingIntervalsRef.current.delete(id);
                const maxTimeout = pollingMaxTimeoutsRef.current.get(id);
                if (maxTimeout) {
                    clearTimeout(maxTimeout);
                    pollingMaxTimeoutsRef.current.delete(id);
                }
            }
        }

        // Start polling for new analyzing designs
        for (const design of analyzingDesigns) {
            if (pollingIntervalsRef.current.has(design.id)) continue;

            const pollDesign = async () => {
                try {
                    const q = `query($id:ID!){ dcDesign(id:$id){ id status current_version { id design_id version_label image_url preview_url dpi width_px height_px width_in height_in notes created_by created_at swatch_fill_mode needs_swatch_fill_choice } insight { id } } }`;
                    const data = await graphqlRequest(authToken, q, { id: design.id });
                    const updated = data?.dcDesign;
                    if (!updated) return;

                    // If analysis is complete (insight exists or status changed), update in-place
                    if (updated.insight || updated.status !== 'in_progress_0') {
                        setPagination((prev) => ({
                            ...prev,
                            designs: prev.designs.map((d) =>
                                d.id === design.id
                                    ? { ...d, status: updated.status, insight: updated.insight, current_version: updated.current_version || d.current_version }
                                    : d
                            ),
                        }));
                        // Stop polling this design
                        const interval = pollingIntervalsRef.current.get(design.id);
                        if (interval) {
                            clearInterval(interval);
                            pollingIntervalsRef.current.delete(design.id);
                        }
                        const maxTimeout = pollingMaxTimeoutsRef.current.get(design.id);
                        if (maxTimeout) {
                            clearTimeout(maxTimeout);
                            pollingMaxTimeoutsRef.current.delete(design.id);
                        }
                    }
                } catch {
                    // Silently ignore polling errors — will retry on next interval
                }
            };

            const interval = setInterval(pollDesign, 4000);
            pollingIntervalsRef.current.set(design.id, interval);

            // Max polling duration: 2 minutes — after that, stop and force-resolve
            const maxTimeout = setTimeout(() => {
                const existingInterval = pollingIntervalsRef.current.get(design.id);
                if (existingInterval) {
                    clearInterval(existingInterval);
                    pollingIntervalsRef.current.delete(design.id);
                }
                pollingMaxTimeoutsRef.current.delete(design.id);
                // Force update — mark the design as having an empty insight so it exits skeleton
                setPagination((prev) => ({
                    ...prev,
                    designs: prev.designs.map((d) =>
                        d.id === design.id ? { ...d, insight: { id: -1 } } : d
                    ),
                }));
            }, 120000);
            pollingMaxTimeoutsRef.current.set(design.id, maxTimeout);
        }

        // Cleanup on unmount
        return () => {
            for (const interval of pollingIntervalsRef.current.values()) {
                clearInterval(interval);
            }
            pollingIntervalsRef.current.clear();
            for (const timeout of pollingMaxTimeoutsRef.current.values()) {
                clearTimeout(timeout);
            }
            pollingMaxTimeoutsRef.current.clear();
        };
    }, [pagination.designs, authToken]);

    const run = async <T>(fn: () => Promise<T>): Promise<T | null> => {
        try {
            setLoading(true);
            return await fn();
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: err?.message || 'Request failed' });
            return null;
        } finally {
            setLoading(false);
        }
    };

    // #region fetchDesignsPaginated
    const fetchDesignsPaginated = async (brand_id?: number, loadMore = false, overrides?: { filters?: DCDesignFilters; sort?: DCDesignSort }) => {
        const resolvedBrandId = brand_id ?? user?.brand_id;
        if (!resolvedBrandId) return;

        const requestId = ++latestRequestIdRef.current;

        if (!loadMore) {
            setPagination({ cursor: null, hasMore: true, designs: [] });
        }

        const activeFilters = overrides?.filters ?? filters;
        const activeSort = overrides?.sort ?? sort;

        setPaginationLoading(true);
        try {
            const q = `query($brand_id:ID!,$first:Int,$after:String,$filters:DCDesignFilters,$sortBy:DCDesignSort){
                dcDesignsPaginated(brand_id:$brand_id,first:$first,after:$after,filters:$filters,sortBy:$sortBy){
                    edges { cursor node { ${paginatedNodeFields} } }
                    pageInfo { hasNextPage hasPreviousPage startCursor endCursor }
                }
            }`;

            const currentPagination = loadMore ? pagination : { cursor: null, hasMore: true, designs: [] as DCDesignPaginatedNode[] };

            const data = await graphqlRequest(authToken, q, {
                brand_id: resolvedBrandId,
                first: 24,
                after: loadMore ? currentPagination.cursor : null,
                filters: {
                    status: activeFilters.status.length > 0 ? activeFilters.status : undefined,
                    pattern_type: activeFilters.pattern_type.length > 0 ? activeFilters.pattern_type : undefined,
                    released: activeFilters.released,
                    title_search: activeFilters.title_search || undefined,
                    needs_attention: activeFilters.needs_attention || undefined,
                    default_wallpaper_scale: activeFilters.default_wallpaper_scale?.length > 0 ? activeFilters.default_wallpaper_scale : undefined,
                    created_by: activeFilters.created_by?.length > 0 ? activeFilters.created_by : undefined,
                    // Only the Licensing Setup page sets this; every other list leaves it empty.
                    licensing_status: activeFilters.licensing_status?.length ? activeFilters.licensing_status : undefined,
                    // Designs licensed from other brands are printable but not editable,
                    // so only the order picker asks for them — Design Studio stays owner-only.
                    include_licensed: scope === DESIGN_LIST_SCOPE_ORDER_PICKER ? true : undefined,
                },
                sortBy: activeSort,
            });

            // Race condition guard: ignore stale responses
            if (requestId !== latestRequestIdRef.current) return;

            const newDesigns: DCDesignPaginatedNode[] = data.dcDesignsPaginated.edges.map((e: any) => e.node);

            setPagination({
                cursor: data.dcDesignsPaginated.pageInfo.endCursor ?? null,
                hasMore: data.dcDesignsPaginated.pageInfo.hasNextPage,
                designs: loadMore ? [...currentPagination.designs, ...newDesigns] : newDesigns,
            });
        } catch (err: any) {
            if (requestId !== latestRequestIdRef.current) return;
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: err?.message || 'Failed to fetch designs' });
        } finally {
            if (requestId === latestRequestIdRef.current) {
                setPaginationLoading(false);
            }
        }
    };

    // #region updateFilters
    const updateFilters = (newFilters: Partial<DCDesignFilters>, brandId?: number) => {
        const merged: DCDesignFilters = { ...filters, ...newFilters };
        setFilters(merged);
        setPagination({ cursor: null, hasMore: true, designs: [] });
        fetchDesignsPaginated(brandId, false, { filters: merged });
    };

    // #region updateSort
    const updateSort = (newSort: Partial<DCDesignSort>, brandId?: number) => {
        const merged: DCDesignSort = { ...sort, ...newSort };
        setSort(merged);
        setPagination({ cursor: null, hasMore: true, designs: [] });
        fetchDesignsPaginated(brandId, false, { sort: merged });
    };

    // #region resetPagination
    const resetPagination = () => {
        setPagination({ cursor: null, hasMore: true, designs: [] });
    };

    // #region fetchDesignsByCollection
    const fetchDesignsByCollection = useCallback(async (
        collectionId: number | null,
        cursor: string | null = null,
        pageSize = 24,
    ): Promise<{ designs: DCDesignPaginatedNode[]; endCursor: string | null; hasNextPage: boolean }> => {
        const resolvedBrandId = user?.brand_id;
        if (!resolvedBrandId) return { designs: [], endCursor: null, hasNextPage: false };

        try {
            const q = `query($brand_id:ID!,$first:Int,$after:String,$filters:DCDesignFilters,$sortBy:DCDesignSort){
                dcDesignsPaginated(brand_id:$brand_id,first:$first,after:$after,filters:$filters,sortBy:$sortBy){
                    edges { cursor node { ${paginatedNodeFields} } }
                    pageInfo { hasNextPage endCursor }
                }
            }`;

            const collectionFilter: Record<string, any> = {};
            // Apply active global filters
            if (filters.status.length > 0) collectionFilter.status = filters.status;
            if (filters.pattern_type.length > 0) collectionFilter.pattern_type = filters.pattern_type;
            if (filters.released) collectionFilter.released = filters.released;
            if (filters.title_search) collectionFilter.title_search = filters.title_search;
            if (filters.default_wallpaper_scale?.length > 0) collectionFilter.default_wallpaper_scale = filters.default_wallpaper_scale;
            if (filters.created_by?.length > 0) collectionFilter.created_by = filters.created_by;

            // Apply collection-specific filter
            if (collectionId != null) {
                collectionFilter.collection_id = Number(collectionId);
            } else {
                collectionFilter.include_uncategorized = true;
            }

            const data = await graphqlRequest(authToken, q, {
                brand_id: resolvedBrandId,
                first: pageSize,
                after: cursor,
                filters: collectionFilter,
                sortBy: sort,
            });

            const designs: DCDesignPaginatedNode[] = data.dcDesignsPaginated.edges.map((e: any) => e.node);
            return {
                designs,
                endCursor: data.dcDesignsPaginated.pageInfo.endCursor ?? null,
                hasNextPage: data.dcDesignsPaginated.pageInfo.hasNextPage,
            };
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            return { designs: [], endCursor: null, hasNextPage: false };
        }
    }, [user?.brand_id, authToken, filters, sort]);

    // #region fetchDesignCreators
    const fetchDesignCreators = async (brand_id?: number) => {
        const resolvedBrandId = brand_id ?? user?.brand_id;
        if (!resolvedBrandId) return [];
        try {
            const q = `query($brand_id:ID!){ dcDesignCreators(brand_id:$brand_id){ id first last } }`;
            const data = await graphqlRequest(authToken, q, { brand_id: resolvedBrandId });
            return (data.dcDesignCreators || []) as { id: number; first: string | null; last: string | null }[];
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            return [];
        }
    };

    /** Designs */
    const listDesigns = (brand_id?: number) => run(async () => {
        const resolvedBrandId = brand_id ?? user?.brand_id;
        if (!resolvedBrandId) {
            setDesigns([]);
            return [];
        }
        const q = `query($brand_id:ID!){ dcDesigns(brand_id:$brand_id){ ${designFields} } }`;
        const data = await graphqlRequest(authToken, q, { brand_id: resolvedBrandId });
        setDesigns(data.dcDesigns);
        return data.dcDesigns as DCDesign[];
    });

    // #region listVariantLinkCandidates
    // Server-side search for the Link Variant dialog. Deliberately does NOT write the shared
    // `designs` state — it returns a filtered subset, not the brand's full list.
    const listVariantLinkCandidates = (brand_id: number, design_id: number, search?: string, first?: number) => run(async () => {
        const q = `query($brand_id:ID!,$design_id:ID!,$search:String,$first:Int){
            dcVariantLinkCandidates(brand_id:$brand_id,design_id:$design_id,search:$search,first:$first){
                has_more
                candidates { linkable reason group_name design { id brand_id title status updated_at current_version { id preview_url image_url } } }
            }
        }`;
        const data = await graphqlRequest(authToken, q, { brand_id, design_id, search: search || null, first: first ?? null });
        return data.dcVariantLinkCandidates as DCVariantLinkCandidateResult;
    });

    const getDesign = (id: number) => run(async () => {
        const q = `query($id:ID!){ dcDesign(id:$id){ ${designFields} } }`;
        const data = await graphqlRequest(authToken, q, { id });
        return data.dcDesign as DCDesign;
    });

    const createDesign = (input: Partial<DCDesign>) => run(async () => {
        const q = `mutation($input:DCDesignInput!){ createDCDesign(input:$input){ ${designFields} } }`;
        const data = await graphqlRequest(authToken, q, { input });
        return data.createDCDesign as DCDesign;
    });

    const updateDesign = (id: number, input: Partial<DCDesign>) => run(async () => {
        const q = `mutation($id:ID!,$input:DCDesignUpdateInput!){ updateDCDesign(id:$id,input:$input){ ${designFields} } }`;
        const data = await graphqlRequest(authToken, q, { id, input });
        return data.updateDCDesign as DCDesign;
    });

    const deleteDesign = async (id: number): Promise<boolean | null | { hasOrders: true; orderCount: number }> => {
        try {
            setLoading(true);
            const q = `mutation($id:ID!){ deleteDCDesign(id:$id){ id } }`;
            await graphqlRequest(authToken, q, { id });
            return true;
        } catch (err: any) {
            const msg = err?.message || '';
            if (msg.startsWith('DESIGN_HAS_ORDERS:')) {
                const count = parseInt(msg.split(':')[1], 10) || 0;
                return { hasOrders: true, orderCount: count };
            }
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: msg || 'Request failed' });
            return null;
        } finally {
            setLoading(false);
        }
    };

    const forceDeleteDesign = (id: number) => run<boolean>(async () => {
        const q = `mutation($id:ID!){ forceDeleteDCDesign(id:$id){ id } }`;
        await graphqlRequest(authToken, q, { id });
        return true;
    });

    /** Versions */
    const listVersions = (design_id: number) => run(async () => {
        const q = `query($design_id:ID!){ dcDesignVersions(design_id:$design_id){ ${versionFields} } }`;
        const data = await graphqlRequest(authToken, q, { design_id });
        setVersions(data.dcDesignVersions);
        return data.dcDesignVersions as DCDesignVersion[];
    });

    const createVersion = (input: Partial<DCDesignVersion>) => run(async () => {
        const q = `mutation($input:DCDesignVersionInput!){ createDCDesignVersion(input:$input){ ${versionFields} } }`;
        const data = await graphqlRequest(authToken, q, { input });
        return data.createDCDesignVersion as DCDesignVersion;
    });

    const deleteVersion = (id: number, design_id: number) => run<boolean>(async () => {
        const q = `mutation($id:ID!,$design_id:ID!){ deleteDCDesignVersion(id:$id,design_id:$design_id){ id } }`;
        await graphqlRequest(authToken, q, { id, design_id });
        return true;
    });

    const setCurrentVersion = (design_id: number, version_id: number) => run(async () => {
        const q = `mutation($design_id:ID!,$version_id:ID!){ setDCDesignCurrentVersion(design_id:$design_id,version_id:$version_id){ ${designFields} } }`;
        const data = await graphqlRequest(authToken, q, { design_id, version_id });
        return data.setDCDesignCurrentVersion as DCDesign;
    });

    /** Comments */
    const listComments = (design_id: number) => run(async () => {
        const q = `query($design_id:ID!){ dcDesignComments(design_id:$design_id){ ${commentFields} } }`;
        const data = await graphqlRequest(authToken, q, { design_id });
        setComments(data.dcDesignComments);
        return data.dcDesignComments as DCDesignComment[];
    });

    const addComment = (input: Partial<DCDesignComment> & { needs_attention?: boolean }) => run(async () => {
        const q = `mutation($input:DCDesignCommentInput!){ addDCDesignComment(input:$input){ ${commentFields} } }`;
        const data = await graphqlRequest(authToken, q, { input: { ...input, user_id: user?.id } });
        return data.addDCDesignComment as DCDesignComment;
    });

    const deleteComment = (id: number) => run<boolean>(async () => {
        const q = `mutation($id:ID!){ deleteDCDesignComment(id:$id){ id } }`;
        await graphqlRequest(authToken, q, { id });
        return true;
    });

    const resolveComment = (id: number, resolvedBy?: number) => run(async () => {
        const q = `mutation($id:ID!,$resolved_by:ID){ resolveDCDesignComment(id:$id,resolved_by:$resolved_by){ ${commentFields} } }`;
        const data = await graphqlRequest(authToken, q, { id, resolved_by: resolvedBy });
        return data.resolveDCDesignComment as DCDesignComment;
    });

    const unresolveComment = (id: number) => run(async () => {
        const q = `mutation($id:ID!){ unresolveDCDesignComment(id:$id){ ${commentFields} } }`;
        const data = await graphqlRequest(authToken, q, { id });
        return data.unresolveDCDesignComment as DCDesignComment;
    });

    /** Colors */
    const listColors = (design_id: number) => run(async () => {
        const q = `query($design_id:ID!){ dcDesignColors(design_id:$design_id){ ${colorFields} } }`;
        const data = await graphqlRequest(authToken, q, { design_id });
        setColors(data.dcDesignColors);
        return data.dcDesignColors as DCDesignColor[];
    });

    const replaceColors = (design_id: number, colorsInput: Partial<DCDesignColor>[]) => run(async () => {
        const q = `mutation($design_id:ID!,$colors:[DCDesignColorInput!]!){ replaceDCDesignColors(design_id:$design_id,colors:$colors){ ${colorFields} } }`;
        const data = await graphqlRequest(authToken, q, { design_id, colors: colorsInput });
        setColors(data.replaceDCDesignColors);
        return data.replaceDCDesignColors as DCDesignColor[];
    });

    /** Insights */
    const getInsight = (design_id: number) => run(async () => {
        const q = `query($design_id:ID!){ dcDesignInsight(design_id:$design_id){ ${insightFields} } }`;
        const data = await graphqlRequest(authToken, q, { design_id });
        setInsight(data.dcDesignInsight);
        return data.dcDesignInsight as DCDesignInsight;
    });

    const upsertInsight = (input: Partial<DCDesignInsight>) => run(async () => {
        const q = `mutation($input:DCDesignInsightInput!){ upsertDCDesignInsight(input:$input){ ${insightFields} } }`;
        const data = await graphqlRequest(authToken, q, { input });
        setInsight(data.upsertDCDesignInsight);
        return data.upsertDCDesignInsight as DCDesignInsight;
    });

    /** Scale options */
    const getScaleOptions = (width_px: number, height_px: number, dpi: number) => run(async () => {
        const q = `query($width_px:Float!,$height_px:Float!,$dpi:Int!){ dcDesignScaleOptions(width_px:$width_px,height_px:$height_px,dpi:$dpi) }`;
        const data = await graphqlRequest(authToken, q, { width_px, height_px, dpi });
        return data.dcDesignScaleOptions as number[];
    });

    const listDesignScaleOptions = (design_id: number) => run(async () => {
        const { data } = await axios.get(`${environment.baseUrl}/api/v2/design/${design_id}/scale-options`, {
            headers: { 'X-Wallmates-Auth': authToken }
        });
        return data as DCDesignScaleOption[];
    });

    /**
     * `replace` clears the design's existing scale options before writing. The
     * server merges on (design_id, scale_value), so without it a stale row whose
     * width is absent from the new set survives — which is how designs ended up
     * offering scales computed from artwork they no longer have.
     */
    const upsertDesignScaleOptions = (design_id: number, options: Partial<DCDesignScaleOption>[], replace?: boolean) => run(async () => {
        try {
            const { data } = await axios.put(
                `${environment.baseUrl}/api/v2/design/${design_id}/scale-options`,
                { options, replace: replace === true },
                { headers: { 'X-Wallmates-Auth': authToken } }
            );
            return data as DCDesignScaleOption[];
        } catch (err: any) {
            // Surface the API's message (e.g. 409 "Cannot disable the 19" wallpaper scale: N open order
            // line item(s) reference it") instead of axios' generic "Request failed with status code".
            const apiMessage = err?.response?.data?.message;
            if (typeof apiMessage === 'string' && apiMessage) err.message = apiMessage;
            throw err;
        }
    });

    const fetchDistinctWallpaperScales = (brand_id?: number) => run(async () => {
        const resolvedBrandId = brand_id ?? user?.brand_id;
        if (!resolvedBrandId) return [];
        const q = `query($brand_id:ID!){ dcDistinctWallpaperScales(brand_id:$brand_id) }`;
        const data = await graphqlRequest(authToken, q, { brand_id: resolvedBrandId });
        return data.dcDistinctWallpaperScales as number[];
    });

    /** Analysis (AI) */
    const generateAnalysis = (params: { design_id: number; image_url: string; width_px: number; height_px: number; dpi: number }) => run(async () => {
        const q = `mutation($design_id:ID!,$image_url:String!,$width_px:Float!,$height_px:Float!,$dpi:Int!){
            generateDCDesignAnalysis(design_id:$design_id,image_url:$image_url,width_px:$width_px,height_px:$height_px,dpi:$dpi){
                colors { ${colorFields} }
                insight { ${insightFields} }
            }
        }`;
        const data = await graphqlRequest(authToken, q, params);
        setColors(data.generateDCDesignAnalysis.colors);
        setInsight(data.generateDCDesignAnalysis.insight);
        return {
            colors: data.generateDCDesignAnalysis.colors as DCDesignColor[],
            insight: data.generateDCDesignAnalysis.insight as DCDesignInsight
        };
    });

    /** Convert */
    const convertToProduct = (design_id: number) => run(async () => {
        const q = `mutation($design_id:ID!){ convertDCDesignToProduct(design_id:$design_id){ ${designFields} } }`;
        const data = await graphqlRequest(authToken, q, { design_id });
        return data.convertDCDesignToProduct as DCDesign;
    });

    const uploadDesignImage = async (file: File): Promise<string | null> => {
        try {
            const q = `mutation($fileName:String!,$contentType:String){ createDCDesignImageUpload(fileName:$fileName,contentType:$contentType){ uploadUrl publicUrl path } }`;
            const data = await graphqlRequest(authToken, q, { fileName: file.name, contentType: file.type || "application/octet-stream" });
            const upload = data?.createDCDesignImageUpload;
            if (!upload?.uploadUrl || !upload?.publicUrl) {
                throw new Error("Failed to create upload URL");
            }

            await axios.put(upload.uploadUrl, file, {
                headers: {
                    "Content-Type": file.type || "application/octet-stream",
                }
            });

            return upload.publicUrl as string;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to upload design image.' });
            return null;
        }
    };

    const hasActiveOrders = async (designId: number): Promise<boolean | null> => {
        return run(async () => {
            const q = `query($design_id:ID!){ dcDesignHasActiveOrders(design_id:$design_id) }`;
            const data = await graphqlRequest(authToken, q, { design_id: designId });
            return data?.dcDesignHasActiveOrders as boolean;
        });
    };

    const reuploadDesignFile = async (designId: number, file: File): Promise<DCDesign | null> => {
        return run(async () => {
            const imageUrl = await uploadDesignImage(file);
            if (!imageUrl) throw new Error('Failed to upload design image');
            const q = `mutation($design_id:ID!,$image_url:String!){
                reuploadDCDesignFile(design_id:$design_id,image_url:$image_url){ ${designFields} }
            }`;
            const data = await graphqlRequest(authToken, q, { design_id: designId, image_url: imageUrl });
            return data?.reuploadDCDesignFile as DCDesign;
        });
    };

    const checkTitleUniqueness = useCallback(async (brandId: number, title: string, excludeDesignId?: number): Promise<{ count: number } | null> => {
        try {
            const q = `query($brand_id:ID!,$title:String!,$exclude_design_id:ID){ checkDCDesignTitleUniqueness(brand_id:$brand_id,title:$title,exclude_design_id:$exclude_design_id){ count } }`;
            const data = await graphqlRequest(authToken, q, { brand_id: brandId, title, exclude_design_id: excludeDesignId });
            return data?.checkDCDesignTitleUniqueness as { count: number };
        } catch {
            return null;
        }
    }, [authToken]);

    return [
        {
            designs,
            versions,
            comments,
            colors,
            insight,
            loading,
            paginatedDesigns: pagination.designs,
            paginationLoading,
            hasMore: pagination.hasMore,
            filters,
            sort,
            groupedDesigns,
            groupedByStatus,
            viewMode,
        },
        {
            setViewMode,
            listDesigns,
            listVariantLinkCandidates,
            getDesign,
            createDesign,
            updateDesign,
            deleteDesign,
            forceDeleteDesign,

            listVersions,
            createVersion,
            deleteVersion,
            setCurrentVersion,

            listComments,
            addComment,
            deleteComment,
            resolveComment,
            unresolveComment,

            listColors,
            replaceColors,

            getInsight,
            upsertInsight,

            getScaleOptions,
            listDesignScaleOptions,
            upsertDesignScaleOptions,
            fetchDistinctWallpaperScales,
            generateAnalysis,
            convertToProduct,
            uploadDesignImage,
            hasActiveOrders,
            reuploadDesignFile,
            fetchDesignsPaginated,
            updateFilters,
            updateSort,
            resetPagination,
            fetchDesignsByCollection,
            fetchDesignCreators,
            checkTitleUniqueness,
        }
    ];
};

